import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  Typography,
  Grid,
  TextareaAutosize,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const SendInvoiceEmail = ({ 
  open, 
  onClose, 
  selectedRow,
  fetchInvoices
}) => {
  const selectedLanguage = selectedRow?.customer?.language || 'sv-se';
  const [formData, setFormData] = useState({
    language: selectedLanguage,
    template: '',
    emailBody: '',
    ccEmail: '',
    bccEmail: '',
    subject: '', 
  });

  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const [originalLanguage, setOriginalLanguage] = useState(null);
  const currentTemplateRef = useRef('');
  const [templates] = useState([
    { value: '1', label: 'Default Invozio Template' },
    { value: '2', label: 'Credit Invoice' },
    { value: '3', label: 'E-Invoice' },
  ]);

  const getTemplateBody = (templateValue) => {
    if (templateValue === '1') {
      return [
        t('Please note: As of May 27, 2025, we are using a new Swedish bankgiro number, managed by PS Betalningsservice. All IBAN payments should be paid to Invozios account at Revolut.'),
        t('You have received an invoice from Invozio in collaboration with PS Betalningsservice, which manages the invoice\'s bankgiro, and TP Finance, which handles salary payments to the affiliated freelancer.'),
        t('The freelancer\'s name is listed as the reference on the invoice. If you have any questions regarding the invoice, pricing, or the work performed, please contact the freelancer directly. They have also received a copy of the invoice.'),
        t('If you have any questions, feel free to contact us:')
      ].join('\n\n');
    } else if (templateValue === '2') {
      return[ t('Here is your credit invoice from Invozio.'),
              t('This invoice has been issued to adjust a previous charge and should be used as supporting documentation in your accounting.'),
              t('If you have any questions or concerns regarding this credit invoice, previous invoices, or anything else related to our services, you are always welcome to reach out to us.'),
      ].join('\n\n');
    } else if (templateValue === '3') {
      return t('Please see the copy of the invoice, the invoice has been sent out as an E-invoice to your customer.');
    }
    return ''; // Return empty string for no template selected
  };

  const languages = [
    { value: 'en-us', label: 'English' },
    { value: 'sv-se', label: 'Swedish' },
  ];

  useEffect(() => {
    if (open && selectedRow) {
      // Store original language when modal opens
    const customerLanguage = selectedRow?.customer?.language || 'sv-se';
    setOriginalLanguage(customerLanguage);
    
    // Set i18n language to customer's language
    i18n.changeLanguage(customerLanguage);
   
      // Set default template to empty
      currentTemplateRef.current = '';
      
      setFormData({
        language:  selectedRow?.customer?.language || 'sv-se',
        template: '',
        emailBody: '',
        ccEmail: '',
        bccEmail: '',
        subject: '', // Set empty initially, will be set by separate useEffect
      });
    }
  }, [open, selectedRow]);

  // Separate useEffect to handle subject translation when language changes
  useEffect(() => {
    if (open && selectedRow) {
      const invoiceType = formData.template === '2' ? t("Credit Invoice") : t("Invoice");
      const translatedSubject = `${invoiceType} #${selectedRow?.invoiceNumber || ''} - ${selectedRow?.customer?.name || selectedRow?.customerName || 'Customer'}`;
      setFormData(prev => ({
        ...prev,
        subject: translatedSubject,
      }));
    }
  }, [open, selectedRow, t, i18n.language, formData.template]);

  // Effect to handle language changes and re-translate email body
  useEffect(() => {
    
    if (currentTemplateRef.current) {
      const emailBodyText = getTemplateBody(currentTemplateRef.current);
      
      
      setFormData(prev => ({
        ...prev,
        emailBody: emailBodyText,
      }));
    }
  }, [i18n.language, t, selectedRow?.invoiceNumber, selectedRow?.customer?.name, selectedRow?.customerName]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If language is changed, update i18n language
    if (field === 'language') {
      i18n.changeLanguage(value);
    }
  };

  const handleTemplateChange = (templateValue) => {
    currentTemplateRef.current = templateValue;
    
    const emailBodyText = getTemplateBody(templateValue);
    
    setFormData(prev => ({
      ...prev,
      template: templateValue,
      emailBody: emailBodyText
    }));
  };

  const handleSendInvoicePDF = async (invoiceNumber, locale, template, emailBody, ccEmail, bccEmail, subject) => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('admin'));
      let token = user?.token || '';
      
      // Convert comma-separated emails to arrays
      const ccEmailArray = ccEmail ? ccEmail.split(',').map(email => email.trim()).filter(email => email) : [];
      const bccEmailArray = bccEmail ? bccEmail.split(',').map(email => email.trim()).filter(email => email) : [];
      
      const requestBody = {
        invoiceNumber: parseInt(invoiceNumber),
        ccEmail: ccEmailArray,
        bccEmail: bccEmailArray,
        subject: subject,
        emailBody: emailBody,
        template: template ? parseInt(template) : 0,
        locale: locale
      };

      const response = await axios.post(`/admin/invoice/send-invoice-email`, requestBody, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      if (response.status === 200) {
        setLoading(false);
        toast.success('Invoice email sent successfully!');
        onClose(); // Close modal on success
        fetchInvoices(); // Refresh invoices list
      } else {
        setLoading(false);
        throw new Error('Failed to Send Email');
      }
    } catch (error) {
      setLoading(false);
      console.error('Email send error:', error);
      toast.error('Failed to send email. Please try again.');
    }
  };

  const handleSend = () => {
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    if (!formData.template || formData.template === '') {
      toast.error('Please select a template');
      return;
    }

    if (!formData.emailBody.trim()) {
      toast.error('Please enter email body content');
      return;
    }

    handleSendInvoicePDF(
      selectedRow?.invoiceNumber,
      formData.language,           
      formData.template,          
      formData.emailBody,
      formData.ccEmail,
      formData.bccEmail,
      formData.subject
    );
  };

  const handleClose = () => {
    // Restore original language when closing
    if (originalLanguage) {
      i18n.changeLanguage(originalLanguage);
    }
    
    setFormData({
      language: 'sv-se',
      template: '',
      emailBody: '',
      ccEmail: '',
      bccEmail: '',
      subject: '',
    });
    onClose();
  };

  const getCreatedByEmail = () => {
    return selectedRow?.createdByEmail || selectedRow?.createdBy;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          minHeight: '600px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Send Invoice Email</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Invoice Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Invoice Information
            </Typography>
            <Box sx={{ backgroundColor: '#dbf2ff', p: 2, borderRadius: 1, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Invoice Number:</Typography>
                  <Typography variant="body1">{selectedRow?.invoiceNumber || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">User ID:</Typography>
                  <Typography variant="body1">{selectedRow?.createdBy || selectedRow?.userId}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Customer Name:</Typography>
                  <Typography variant="body1">{selectedRow?.customer?.name || selectedRow?.customerName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">User Name:</Typography>
                  <Typography variant="body1">{selectedRow?.userName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Created By:</Typography>
                  <Typography variant="body1">{getCreatedByEmail()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Email To:</Typography>
                  <Typography variant="body1">{selectedRow?.customer?.email|| selectedRow?.email}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Language Selection */}
          <Grid item xs={6}>
            <FormControl fullWidth>
            <Typography variant="subtitle2" gutterBottom>
              Language
            </Typography>
              <Select
                value={formData.language}
                onChange={(e) => handleInputChange('language', e.target.value)}
                style={{ border: '1px solid #c4c4c4',borderRadius: '4px'}}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Template Selection */}
          <Grid item xs={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2" gutterBottom>
               Template
              </Typography>
              <Select
                value={formData.template}
                onChange={(e) => handleTemplateChange(e.target.value)}
                style={{ border: '1px solid #c4c4c4',borderRadius: '4px'}}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected === '') {
                    return <span style={{ color: '#999' }}>Select Template</span>;
                  }
                  const selectedTemplate = templates.find(t => t.value === selected);
                  return selectedTemplate ? selectedTemplate.label : selected;
                }}
              >
                {templates.map((template) => (
                  <MenuItem key={template.value} value={template.value} >
                    {template.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Subject */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Subject
            </Typography>
            <TextareaAutosize
              fullWidth
              value={formData.subject}
              style={{ border: '1px solid #c4c4c4',borderRadius: '4px',minHeight: '50px',width: '100%',padding: '12px',fontSize: '1rem' , outline: 'none', '&:focus': { borderColor: '#1976d2' }}}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Enter email subject"
              required
            />
          </Grid>

          {/* CC Email - Hide when template 3 is selected */}
          {formData.template !== '3' && (
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                CC
              </Typography>
              <TextField
                fullWidth
                value={formData.ccEmail}
                style={{ border: '1px solid #c4c4c4',borderRadius: '4px'}}
                onChange={(e) => handleInputChange('ccEmail', e.target.value)}
                placeholder="Enter CC emails separated by commas"
              />
            </Grid>
          )}

          {/* BCC Email - Hide when template 3 is selected */}
          {formData.template !== '3' && (
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                BCC
              </Typography>
              <TextField
                fullWidth
                value={formData.bccEmail}
                style={{ border: '1px solid #c4c4c4',borderRadius: '4px'}}
                onChange={(e) => handleInputChange('bccEmail', e.target.value)}
                placeholder="Enter BCC emails separated by commas"
              />
            </Grid>
          )}

          {/* Email Body */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Email Body
            </Typography>
            <TextareaAutosize
              minRows={10}
              placeholder="Enter your email content here..."
              value={formData.emailBody}
              onChange={(e) => handleInputChange('emailBody', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #c4c4c4',
                borderRadius: '4px',
                fontFamily: 'inherit',
                fontSize: '1rem',
                resize: 'vertical',
                minHeight: '250px',
                maxHeight: '400px',
                overflow: 'auto',
                outline: 'none',
                '&:focus': {
                  borderColor: '#1976d2',
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSend}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendInvoiceEmail;
